// src/pages/auth/SecuritySetupPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setFirstLogin } from '../../store/slices/authSlice';
import { fetchQuestions } from '../../store/slices/securityQuestionSlice';
import { Shield, Key, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import securityQuestionApi from 'api/securityQuestionApi';

const SecuritySetupPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { questions, isLoading: qLoading, error: qError } = useSelector(
    (state) => state.securityQuestion
  );

  const [answers, setAnswers] = useState({}); // { questionId: "answer" }
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Load questions
  useEffect(() => {
    dispatch(fetchQuestions());
  }, [dispatch]);

  useEffect(() => {
    if (qError) toast.error(qError);
  }, [qError]);

  // Validation
  const validate = () => {
    const e = {};
    questions.forEach((q) => {
      if (!answers[q.id]?.trim()) {
        e[`answer_${q.id}`] = 'Answer required';
      }
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const answersArray = Object.entries(answers).map(([question, answer]) => [
        question,
        answer,
      ]);

      await securityQuestionApi.firstLoginSetup(answersArray);

      dispatch(setFirstLogin(false));
      toast.success('Security setup complete!');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.answers?.[0] ||
        err.message ||
        'Setup failed.';
      toast.error(msg);
      setErrors({ general: msg });
    } finally {
      setSubmitting(false);
    }
  };

  // Loading UI
  if (qLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading questions…</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent mb-4 shadow-lg">
            <Shield className="w-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold font-['Space_Grotesk'] mb-2">
            First-Time Security Setup
          </h1>
          <p className="text-muted-foreground">
            Answer all security questions to complete your account setup
          </p>
        </div>

        <Card className="shadow-xl animate-slide-in">
          <CardHeader>
            <CardTitle className="text-2xl">Security Questions</CardTitle>
            <CardDescription>
              Provide answers to every question below
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* One input per question */}
              {questions.map((q) => (
                <div key={q.id} className="space-y-2">
                  <Label>{q.question}</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Your answer"
                      value={answers[q.id] || ''}
                      onChange={(e) =>
                        setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))
                      }
                      className={cn(
                        'pl-10',
                        errors[`answer_${q.id}`] && 'border-destructive'
                      )}
                    />
                  </div>
                  {errors[`answer_${q.id}`] && (
                    <p className="text-sm text-destructive">{errors[`answer_${q.id}`]}</p>
                  )}
                </div>
              ))}

              {/* General error */}
              {errors.general && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                  <p className="text-sm text-destructive">{errors.general}</p>
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                className="w-full"
                disabled={submitting || qLoading}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Setting up…
                  </>
                ) : (
                  'Complete Setup'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SecuritySetupPage;